import { Controller } from "@hotwired/stimulus"

function decimalPrice(rawPrice) {
  const price = String(rawPrice ?? "").trim()
  if (!/^\d{1,12}(\.\d+)?$/.test(price)) return null
  const [whole, fraction = ""] = price.split(".")
  return { units: BigInt(whole + fraction), scale: 10n ** BigInt(fraction.length) }
}

// Integer arithmetic keeps decimal vendor prices exact at whole-dollar boundaries.
export function discountedDollars(rawPrice, percent) {
  const price = decimalPrice(rawPrice)
  const discount = String(percent ?? "").trim()
  if (!price || !/^\d{1,3}(\.\d{1,2})?$/.test(discount)) return null
  const [rate, decimals = ""] = discount.split(".")
  const rateScale = 10n ** BigInt(decimals.length)
  const numerator = BigInt(rate + decimals)
  if (numerator > 100n * rateScale) return null
  return price.units * numerator / (price.scale * rateScale * 100n)
}

export default class extends Controller {
  static targets = ["discount", "discountError", "search", "sort", "list", "row", "count", "empty"]

  connect() {
    // Retain the server's name/URL/ID order, including across reconnects.
    this.alphabeticalRows ||= this.rowTargets
    this.updateDiscount()
    this.filter()
    this.sort()
  }

  sort() {
    this.dispatch("change")
    const rows = this.alphabeticalRows.map((row, index) => ({ row, index, price: decimalPrice(row.dataset.price) }))
    if (this.sortTarget.value === "price_desc") {
      rows.sort((a, b) => {
        if (!a.price || !b.price) return Number(!a.price) - Number(!b.price) || a.index - b.index
        const difference = b.price.units * a.price.scale - a.price.units * b.price.scale
        return difference > 0n ? 1 : difference < 0n ? -1 : a.index - b.index
      })
    }
    // Moving the existing nodes preserves visibility, raw prices and discounts.
    const fragment = document.createDocumentFragment()
    rows.forEach(({ row }) => fragment.appendChild(row))
    this.listTarget.appendChild(fragment)
  }

  updateDiscount() {
    const valid = discountedDollars("0", this.discountTarget.value) !== null
    this.discountTarget.setAttribute("aria-invalid", String(!valid))
    this.discountErrorTarget.hidden = valid
    this.rowTargets.forEach(row => {
      const dollars = valid ? discountedDollars(row.dataset.price, this.discountTarget.value) : null
      row.querySelector("[data-discounted-price]").textContent = dollars === null ? "—" : `$\u00a0${dollars.toLocaleString("en-US")}`
    })
  }

  filter() {
    this.dispatch("change")
    const query = this.searchTarget.value.trim().toLowerCase()
    const collectorQuery = query.replace(/^#\s*/, "")
    let count = 0
    this.rowTargets.forEach(row => {
      const matches = !query || row.dataset.name.toLowerCase().includes(query) ||
        (row.dataset.tags || "").toLowerCase().includes(query) ||
        (collectorQuery && row.dataset.collector.toLowerCase().includes(collectorQuery))
      row.hidden = !matches
      if (matches) count += 1
    })
    this.countTarget.textContent = `${count} of ${this.rowTargets.length} results`
    this.emptyTarget.hidden = count !== 0
  }
}
